// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { OptionsSelector } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { SilverAlphaAdvisorCard } from './silver-alpha-advisor-card';
import { SilverAlphaPendingProposalCard } from './silver-alpha-pending-proposal-card';
import { SilverAlphaStrategyCard } from './silver-alpha-strategy-card';
import styles from './silver-alpha-home.module.css';
import { useSilverAlphaHome } from './use-silver-alpha-home';

export const SilverAlphaHome = defineComponent({
  displayName: 'SilverAlphaHome',
  useSetup: useSilverAlphaHome,
  View: ({
    advisorOptions,
    selectedAdvisorId,
    selectedAdvisor,
    onAdvisorChangeValue,
    showAdvisorSelector,
    advisorDefaultText,
    hasActiveProposal,
    activeProposal,
    featuredPendingProposal,
    otherProposals,
    hasProposals,
  }) => (
    <div className={styles.home} data-testid="silver-alpha-home">
      <div className={styles.layout}>
        <div
          className={`${styles.headerRow} ${
            hasActiveProposal ? styles.headerRowActive : ''
          }`}
        >
          <header className={styles.headerBlock}>
            {hasActiveProposal ? (
              <>
                <h1 className={styles.title} style={{ marginTop: 0 }}>
                  <Translation
                    i18nKey="silver-alpha-home.page-title"
                    ns="components"
                  />
                </h1>
                <p className={styles.subtitle}>
                  <Translation
                    i18nKey="silver-alpha-home.page-subtitle"
                    ns="components"
                  />
                </p>
              </>
            ) : (
              <>
                <p className={styles.eyebrow}>
                  <Translation
                    i18nKey="silver-alpha-home.proposal-received"
                    ns="components"
                  />
                </p>
                <h1 className={styles.title}>
                  <Translation
                    i18nKey="silver-alpha-home.proposals-title"
                    ns="components"
                  />
                </h1>
                <p className={styles.subtitle}>
                  <Translation
                    i18nKey="silver-alpha-home.proposals-subtitle"
                    ns="components"
                  />
                </p>
              </>
            )}
          </header>

          {showAdvisorSelector ? (
            <div className={styles.selector}>
              <OptionsSelector
                dataTestId="silver-alpha-advisor-select"
                defaultText={advisorDefaultText}
                onChangeValueSelected={onAdvisorChangeValue}
                optionSelected={selectedAdvisorId}
                optionsAvailable={advisorOptions}
                selectType="gray"
              />
            </div>
          ) : null}
        </div>

        {!hasProposals ? (
          <p className={styles.empty} data-testid="silver-alpha-home-empty">
            <Translation i18nKey="silver-alpha-home.empty" ns="components" />
          </p>
        ) : hasActiveProposal && activeProposal ? (
          <>
            <div
              className={styles.activeGrid}
              data-testid="silver-alpha-active-layout"
            >
              {selectedAdvisor ? (
                <SilverAlphaAdvisorCard advisor={selectedAdvisor} />
              ) : null}
              <SilverAlphaStrategyCard proposal={activeProposal} />
            </div>

            {otherProposals.length > 0 ? (
              <section
                className={styles.otherSection}
                data-testid="silver-alpha-other-proposals"
              >
                <h2 className={styles.otherTitle}>
                  <Translation
                    i18nKey="silver-alpha-home.other-proposals"
                    ns="components"
                  />
                </h2>
                {otherProposals.map(proposal => (
                  <SilverAlphaPendingProposalCard
                    key={proposal.id}
                    proposal={proposal}
                  />
                ))}
              </section>
            ) : null}
          </>
        ) : (
          <>
            {featuredPendingProposal ? (
              <div data-testid="silver-alpha-pending-layout">
                <SilverAlphaPendingProposalCard
                  proposal={featuredPendingProposal}
                />
              </div>
            ) : null}

            {otherProposals.length > 0 ? (
              <section
                className={styles.otherSection}
                data-testid="silver-alpha-other-proposals"
              >
                <h2 className={styles.otherTitle}>
                  <Translation
                    i18nKey="silver-alpha-home.other-proposals"
                    ns="components"
                  />
                </h2>
                {otherProposals.map(proposal => (
                  <SilverAlphaPendingProposalCard
                    key={proposal.id}
                    proposal={proposal}
                  />
                ))}
              </section>
            ) : null}
          </>
        )}
      </div>
    </div>
  ),
});
